import { MouseEvent, use, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Date } from '../../../../../common/src/calendar.ts';
import { SYP_LITURGICAL_BGCOLORS } from '../../../../../common/src/color.ts';
import {
  addDayOffset,
  dateCompare,
  dateFromString,
  DateString,
  dateStringFromDate,
  DayOfWeek,
  DaysOfWeek,
  getDayOfMonth,
  getDayOfWeek,
  getMonthName,
  getMonthNameFromRawDate,
  getYear,
  thisSundayDate,
  todayDate,
} from '../../../../../common/src/date_string.ts';
import { CalendarSongListType, EventSongList, SongListType } from '../../../../../common/src/model.ts';
import { getSongListRecurrencesByDate } from '../../../../../common/src/song_list_recurrences.ts';
import { capitalize } from '../../../../../common/src/util.ts';
import { HymnalWithHymns } from '../../../data/hymnals.ts';
import { clearSongListIdMap } from '../../../data/song_list_id_map.tsx';
import { synchronizeAllSongLists } from '../../../data/use_current_org.tsx';
import { HymnalsContext } from '../../../data/use_hymnals.tsx';
import { useUserAttributes } from '../../../data/use_user_attributes.ts';
import { goBack } from '../../../util/go_back.ts';
import { getDisplayNameForSongListEntry } from '../../../util/path.ts';
import { getPlatform, Platform } from '../../../util/platform.ts';
import { UploadSongButton } from '../../../util/upload_song_button.tsx';
import { useCurrentDate } from '../../../util/use_current_date.tsx';
import { EditEventProps, EventEditor } from './event_editor.tsx';
import { BackButton, BackButtonPlaceholder, DisplayUnauthorizedAlert, isAuthorizedToEditSongList } from './shared.tsx';
import { AllSongLists, useAllSongLists } from './use_all_song_lists.ts';

const PastWeeks = 2 * 52;
const FutureWeeks = 52;

// only one event per day for now - would need to adjust song_lists database constraint to support multiple
const allowMultipleEventsPerDay = false;

interface Year {
  year: number;
  months: Month[];
}

interface Month {
  name: string;
  year: number;
  dates: Array<DateString | undefined>;
}

export function Calendar({ songListType }: { songListType: CalendarSongListType }) {
  const allSongLists = useAllSongLists();
  const navigate = useNavigate();
  const [editEventProps, setEditEventProps] = useState<EditEventProps | undefined>();
  const { isInternalUser, isOrgAdmin, isStaff } = useUserAttributes();
  const hymnals = use(HymnalsContext);
  const today = useCurrentDate();
  const relevantSongLists = useMemo(
    () => allSongLists.filter(({ type }) => type === songListType) as EventSongList[],
    [allSongLists, songListType],
  );
  const seasonalSongListsByDate = useMemo(() => {
    const maxDate = addDayOffset(thisSundayDate(today), (FutureWeeks + 1) * 7);
    return getSongListRecurrencesByDate(relevantSongLists, maxDate);
  }, [relevantSongLists, today]);

  const allYears = useMemo(() => {
    return getAllYears();
  }, []);

  function getMonthElementId(year: number, monthName: string) {
    return `event-calendar-${year}-${monthName}`;
  }

  useEffect(() => {
    void synchronizeAllSongLists();
    const scrollTimeout = setTimeout(() => {
      const date = thisSundayDate(todayDate());
      document.getElementById(getMonthElementId(getYear(date), getMonthName(date)))?.scrollIntoView({
        block: 'center',
        behavior: 'instant',
      });
    }, 100);
    return () => clearTimeout(scrollTimeout);
  }, []);

  const onBack = useCallback(() => {
    void (async () => {
      await synchronizeAllSongLists();
      clearSongListIdMap();
      await goBack(navigate);
    })();
  }, [navigate]);

  const onCloseEventEditor = useCallback(() => {
    setEditEventProps(undefined);
  }, []);

  const onOpenEventEditor = useCallback(
    (date: DateString, songListId: number) => {
      if (!isAuthorizedToEditSongList({ isOrgAdmin, isInternalUser, isStaff, songListType })) {
        void DisplayUnauthorizedAlert();
        return;
      }
      let defaultName: string | undefined = undefined;
      const newEvent = songListId < 0;
      if (newEvent) {
        const allNamedEvents = (
          allSongLists.filter(({ type, name }) => type === songListType && name !== undefined) as EventSongList[]
        ).sort((lhs, rhs) => dateCompare(lhs.date, rhs.date));
        const sameDayEvents = allNamedEvents.filter((list) => getDayOfWeek(list.date) === getDayOfWeek(date));
        defaultName =
          sameDayEvents.length > 0
            ? sameDayEvents[sameDayEvents.length - 1].name
            : allNamedEvents.length > 0
              ? allNamedEvents[allNamedEvents.length - 1].name
              : undefined;
      }

      setEditEventProps({ defaultName, songListId, onClose: onCloseEventEditor, songListType });
    },
    [isOrgAdmin, isInternalUser, isStaff, songListType, onCloseEventEditor, allSongLists],
  );

  const dateToHighlight = today;

  return (
    <>
      <OuterScrollWrapper>
        {allYears.map((year) => (
          <YearWrapper key={year.year}>
            <YearHeader>
              <BackButton onClick={onBack} />
              <BackButtonPlaceholder />
              <YearNumber>
                {' '}
                {`${year.year} ${capitalize(songListType)} Calendar${allSongLists.containsUpdates ? ' *' : ''}`}
              </YearNumber>
              <UploadSongButton />
            </YearHeader>
            {year.months.map((month) => (
              <MonthWrapper key={month.name} id={getMonthElementId(year.year, month.name)}>
                <MonthHeader>
                  <MonthName>{month.name}</MonthName>
                  <WeekGrid>
                    {DaysOfWeek.map((dayOfWeek) => (
                      <DayOfWeekElement key={dayOfWeek}>{dayOfWeek.slice(0, 3)}</DayOfWeekElement>
                    ))}
                  </WeekGrid>
                </MonthHeader>
                <WeekGrid>
                  {month.dates.map((date, index) => {
                    if (date === undefined) {
                      // Calendar-grid filler for days outside the current month; positions are stable.
                      // eslint-disable-next-line @eslint-react/no-array-index-key
                      return <div key={`empty-${month.year}-${month.name}-${index}`} />;
                    }
                    return (
                      <Day
                        key={date}
                        songLists={seasonalSongListsByDate[date] ?? []}
                        allSongLists={allSongLists}
                        date={date}
                        dateToHighlight={dateToHighlight}
                        onOpenEventEditor={onOpenEventEditor}
                        songListType={songListType}
                        hymnals={hymnals}
                      />
                    );
                  })}
                </WeekGrid>
              </MonthWrapper>
            ))}
          </YearWrapper>
        ))}
      </OuterScrollWrapper>
      {editEventProps && <EventEditor {...editEventProps} />}
    </>
  );
}

function Day({
  songLists,
  allSongLists,
  date,
  dateToHighlight,
  onOpenEventEditor,
  songListType,
  hymnals,
}: {
  songLists: EventSongList[];
  allSongLists: AllSongLists;
  date: DateString;
  dateToHighlight: DateString;
  onOpenEventEditor: (date: DateString, songListId: number) => void;
  songListType: CalendarSongListType;
  hymnals: HymnalWithHymns[];
}) {
  const onOpenExistingEvent = useCallback(() => {
    const { id } = songLists[0];
    if (id) {
      setTimeout(() => onOpenEventEditor(date, id));
    }
  }, [date, onOpenEventEditor, songLists]);

  const onAddEvent = useCallback(async () => {
    const songListId = await allSongLists.update({
      date,
      type: songListType,
      songs: [],
    });
    if (songListId !== undefined) {
      setTimeout(() => onOpenEventEditor(date, songListId));
    }
  }, [allSongLists, date, onOpenEventEditor, songListType]);

  const onDayClick = useCallback(() => {
    if (!allowMultipleEventsPerDay && songLists.length > 0) {
      return onOpenExistingEvent();
    }
    void onAddEvent();
  }, [onOpenExistingEvent, onAddEvent, songLists.length]);

  const onEditEvent = useCallback(
    (songList: EventSongList) => (event: MouseEvent<HTMLElement>) => {
      const { id } = songList;
      if (!id) {
        console.log(`Expected ID for song list: ${JSON.stringify(songList)}`);
        return;
      }
      setTimeout(() => onOpenEventEditor(date, id));
      event.stopPropagation();
    },
    [date, onOpenEventEditor],
  );

  const allowNewEvent = allowMultipleEventsPerDay || songLists.length === 0;
  const day = Date.fromString(date);
  const dayName = day.label('#F', '#-O #D #P #S').replace(/^The (?=[BNRS])/, '');
  const bgColor = dayName.length > 0 ? day.bgColor(day.isSunday) : undefined;

  return (
    <DayWrapper key={date} onClick={onDayClick}>
      <DayTopBorderWrapper>
        <DayTopBorder />
      </DayTopBorderWrapper>
      <DayHeader $bgColor={bgColor}>
        <SundayTitle>{dayName}</SundayTitle>
        {allowNewEvent && <AddEventButton>New</AddEventButton>}
        <DayOfMonthWrapper $highlight={date === dateToHighlight}>
          <DayOfMonth $highlight={date === dateToHighlight}>{getDayOfMonth(date)}</DayOfMonth>
        </DayOfMonthWrapper>
      </DayHeader>
      {songLists.map((songList) => (
        <Event key={songList.id} onClick={onEditEvent(songList)}>
          {getSongListDisplayName(songList, hymnals)}
        </Event>
      ))}
    </DayWrapper>
  );
}

function getSongListDisplayName(songList: EventSongList, hymnals: HymnalWithHymns[]): string {
  if (songList.type === SongListType.Seasonal) {
    return getDisplayNameForSongListEntry({ entry: songList.songs[0], hymnals }) ?? 'Entry';
  }
  return songList.name ?? 'Event';
}

function getAllYears(): Year[] {
  const years: Year[] = [];
  const firstDate = addDayOffset(thisSundayDate(todayDate()), -(PastWeeks * 7));
  const date = dateFromString(firstDate);
  let currentYear: Year = { year: date.getFullYear(), months: [] };
  let currentMonth: Month = {
    name: getMonthNameFromRawDate(date),
    year: date.getFullYear(),
    dates: [],
  };
  let { dates } = currentMonth;
  for (let week = 0; week < PastWeeks + FutureWeeks; week++) {
    for (let day = 0; day < 7; day++) {
      if (getMonthNameFromRawDate(date) !== currentMonth.name) {
        // fill in blanks at end of current month
        const lastDayOfMonth = getDayOfWeek(dates[dates.length - 1] as DateString);
        for (let dayOfWeek = DayOfWeek.Saturday; dayOfWeek !== lastDayOfMonth; dayOfWeek--) {
          dates.push(undefined);
        }

        currentYear.months.push(currentMonth);
        currentMonth = { name: getMonthNameFromRawDate(date), year: date.getFullYear(), dates: [] };
        if (currentYear.year !== date.getFullYear()) {
          years.push(currentYear);
          currentYear = { year: date.getFullYear(), months: [] };
        }
        ({ dates } = currentMonth);

        // fill in blanks at beginning of new month
        const firstDayOfMonth = date.getDay();
        for (let dayOfWeek = DayOfWeek.Sunday; dayOfWeek !== firstDayOfMonth; dayOfWeek++) {
          currentMonth.dates.push(undefined);
        }
      }
      dates.push(dateStringFromDate(date));
      date.setDate(date.getDate() + 1);
    }
  }
  currentYear.months.push(currentMonth);
  years.push(currentYear);
  return years;
}

const OuterScrollWrapper = styled.div`
  overflow: scroll;
  overscroll-behavior-x: none; /* avoid horizontal dialog scroll on iOS */
  height: 100vh;
`;
const YearWrapper = styled.div`
  isolation: isolate; // new stacking context to isolate child z-index's
  background-color: var(--color-background);
`;
// for sticky positioning
const YearHeaderHeight = '3rlh';
const YearHeader = styled.div`
  z-index: 4;
  position: sticky;
  top: 0;
  background-color: var(--color-background);
  display: flex;
  align-items: center;
  height: ${YearHeaderHeight};
`;
const YearNumber = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.7rem;
  text-align: left;
  text-wrap: nowrap;
  overflow: hidden;
  max-width: 85vw;
`;
const MonthWrapper = styled.div``;
// for sticky positioning
const MonthHeaderHeight = '4rlh';
const MonthHeader = styled.div`
  z-index: 3;
  position: sticky;
  top: ${YearHeaderHeight};
  background-color: var(--color-background);
  margin-bottom: -1px;
  height: ${MonthHeaderHeight};
  overflow: hidden;
`;
const MonthName = styled.div`
  padding-left: 0.5rem;
  font-size: 2rem;
  text-align: left;
`;
const WeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  grid-gap: 1px; /* avoids cell outline over-reach */
  padding: 3px; /* allows left & right cell outlines to be visible*/
`;
const AddEventButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  font-size: 0.8rem;
  visibility: hidden;
  color: var(--color-background);
  background-color: var(--color-text);
  border-radius: 50px;
  padding: 0 10px;
  border: none;
  margin: 2px;
  cursor: pointer;
`;
const DayMinHeight = '3.5lh';
const DayWrapper = styled.div`
  position: relative;
  outline: 1px solid var(--color-text); /* outline instead of border takes up 0 space */
  display: flex;
  flex-direction: column;
  min-height: ${DayMinHeight};
  cursor: pointer;

  ${
    getPlatform() !== Platform.Mobile &&
    `
    &:hover ${AddEventButton} {
      visibility: revert;
    }
    &:hover {
      z-index: 2;
      outline: 3px solid var(--color-text);
    }
  `
  }
`;
const DayOfWeekElement = styled.div`
  z-index: 3;
  position: sticky;
  background-color: var(--color-background);
`;
const DayTopBorderWrapper = styled.div`
  position: absolute;
  height: ${DayMinHeight};
  width: 100%;
  pointer-events: none;
`;
const DayTopBorder = styled.div`
  height: 0;
  margin-top: -1px;
  border-top: 1px solid var(--color-text);
  position: sticky;
  top: calc(${YearHeaderHeight} + ${MonthHeaderHeight});
`;
const DayHeader = styled.div<{ $bgColor: string | undefined }>`
  display: flex;
  min-height: 1.12lh;
  border-bottom: 1px solid #e0e0e0;
  background-color: var(--color-background-day-header);
  ${({ $bgColor }) => $bgColor && `background-color: ${$bgColor};`}
  ${({ $bgColor }) => $bgColor && 'color: var(--color-text-black)'};
  ${({ $bgColor }) => ($bgColor === SYP_LITURGICAL_BGCOLORS.black ? 'color: white' : '')};
`;

const DayHeaderTopMargin = '2px';

const SundayTitle = styled.div`
  width: calc(100% - 2em);
  text-align: left;
  text-wrap: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  margin-top: ${DayHeaderTopMargin};
  margin-left: 3px;
`;

const DayOfMonthWrapper = styled.div<{ $highlight: boolean }>`
  flex-shrink: 0;
  position: absolute;
  top: 0;
  right: 0;
  margin: ${DayHeaderTopMargin} 6.5px;
  ${(props) =>
    !props.$highlight
      ? ''
      : `
    color: white;
    background-color: var(--color-date-highlight-background);
    border-radius: 20px;
    padding: 0 5px;
    margin: 1px;
  `}
`;
const DayOfMonth = styled.div<{ $highlight: boolean }>`
  text-align: right;
  ${(props) =>
    !props.$highlight
      ? ''
      : `
    min-width: 1.5ch;
    text-align: center;
  `}
`;
const Event = styled.button`
  background-color: var(--color-background-event);
  color: var(--color-text);
  border: 2px solid var(--color-text);
  border-radius: 10px;
  margin: 0 2px;
  padding: 0 8px;
  overflow: hidden;
  ${
    allowMultipleEventsPerDay
      ? `
      text-wrap: nowrap;
    `
      : `
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
    `
  }
  text-overflow: ellipsis;
  cursor: pointer;
  line-height: 150%;
`;
